import React, { useState, useEffect, useCallback } from 'react';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import ActionDropdown from '../../components/ui/ActionDropdown';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { cn } from '../../utils/cn';
import { CanCreate, CanEdit, CanDelete, PermissionGuard } from '../../components/auth/PermissionGuard';
import { useClampPage } from '../../hooks/useClampPage';
import { usePermission } from '../../hooks/usePermission';
import {
  RiAddLine,
  RiSearchLine,
  RiFilterLine,
  RiFileDownloadLine,
  RiFileUploadLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiRefreshLine,
  RiFileTextLine,
  RiFileListLine,
  RiCalendarCheckLine,
  RiCalendarCloseLine,
  RiTimeLine,
  RiFileChartLine,
  RiUserLine,
  RiQuillPenLine,
  RiExchangeLine,
  RiMore2Fill,
  RiMailSendLine,
  RiCheckLine,
  RiCloseLine,
} from 'react-icons/ri';
import contractService, { Contract, ContractStatus, GetContractsParams, contractKindLabel, isOpenEndedContract } from '../../services/contractService';
import AddendumFormModal from '../../components/contract/AddendumFormModal';
import companyService from '../../services/companyService';
import { showAlert, showConfirm } from '../../utils/alert';
import { formatDate, calculateRemainingDays } from '../../utils/dateUtils';
import Pagination from '../../components/ui/Pagination';
import DualScrollWrapper from '../../components/ui/DualScrollWrapper';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { queryKeys } from '../../queryClient';
import { useCreateContract, useUpdateContract, useDeleteContract } from '../../hooks/mutations/useContractMutations';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { ContractStatusBadge } from '../../components/ui/StatusBadge';
import { terminateContractConfirmMessage, rejectContractConfirmMessage, deleteContractConfirmMessage } from '../../utils/contractConfirm';

// สถานะการค้นหาและกรอง
interface FilterState {
  search: string;
  status?: ContractStatus;
  company_id?: number;
  site_id?: number;
  from_date?: string;
  to_date?: string;
  is_expiring_soon?: boolean;
  days_to_expiry?: number;
}

/** ช่วง "ใกล้หมดอายุ" ที่หน้านี้เสนอ (ปุ่มลัด 7/30 + dropdown 7/15/30/60) — ค่าอื่นจาก URL ไม่รับ */
const EXPIRING_DAY_OPTIONS = [7, 15, 30, 60];

/** เซ็นแล้ว = มี customer_signed_at (POST /sign) หรือเอกสารถึงขั้นลูกค้าเซ็น/เสร็จสมบูรณ์ (ระบบติดตามเอกสาร) */
const isContractSigned = (c: { customer_signed_at?: string | null; document_status?: string | null }) =>
  !!c.customer_signed_at || c.document_status === 'CUSTOMER_SIGNED' || c.document_status === 'COMPLETED';

const ContractPage: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canCreate, canEdit, canDelete, hasPermission } = usePermission();

  // BUG-181 FIX: Two-way URL filter sync using useSearchParams
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatusParam = searchParams.get('status');
  // OBS-S1104-001/OBS-S1107-001 FIX (S1108 propagation from RentalPage): read `page` param from URL for refresh-state-restore
  const pageParam = searchParams.get('page');

  // State
  const [showFilters, setShowFilters] = useState<boolean>(!!urlStatusParam);
  const [filters, setFilters] = useState<FilterState>(() => {
    const base: FilterState = { search: '' };
    if (urlStatusParam === 'expiring') {
      // BUG-S2074-001: เดิมคืน 30 ตายตัว → กด "หมดอายุใน 7 วัน" แล้วรีเฟรช/แชร์ลิงก์ ได้ช่วง 30 วันเงียบ ๆ
      const urlDays = Number(searchParams.get('days'));
      const days = EXPIRING_DAY_OPTIONS.includes(urlDays) ? urlDays : 30;
      return { ...base, status: ContractStatus.ACTIVE, is_expiring_soon: true, days_to_expiry: days };
    }
    if (urlStatusParam === 'active') return { ...base, status: ContractStatus.ACTIVE };
    if (urlStatusParam === 'expired') return { ...base, status: ContractStatus.EXPIRED };
    if (urlStatusParam === 'terminated') return { ...base, status: ContractStatus.TERMINATED };
    if (urlStatusParam === 'draft') return { ...base, status: ContractStatus.DRAFT };
    return base;
  });

  // OBS-S1104-001 FIX (S1108): Sync filter + page state back to URL
  // page=1 ไม่ใส่ใน URL (clean URL) · page>1 ใส่ใน URL · invalid → fail-open to page 1
  const syncFiltersToUrl = useCallback((currentFilters: FilterState, page: number) => {
    const newParams = new URLSearchParams();
    if (currentFilters.is_expiring_soon) {
      newParams.set('status', 'expiring');
      // 30 = ค่าเริ่มต้น ไม่ใส่ (ลิงก์เดิม ?status=expiring ยังได้ผลเท่าเดิม)
      if (currentFilters.days_to_expiry && currentFilters.days_to_expiry !== 30) {
        newParams.set('days', String(currentFilters.days_to_expiry));
      }
    } else if (currentFilters.status) {
      const statusMap: Record<string, string> = {
        [ContractStatus.ACTIVE]: 'active',
        [ContractStatus.EXPIRED]: 'expired',
        [ContractStatus.TERMINATED]: 'terminated',
        [ContractStatus.DRAFT]: 'draft',
      };
      const urlStatus = statusMap[currentFilters.status];
      if (urlStatus) newParams.set('status', urlStatus);
    }
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  // OBS-S1104-001 FIX (S1108): read `page` from URL on init + defensive parse + fail-open to 1
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!pageParam) return 1;
    const n = parseInt(pageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });

  useEffect(() => {
    syncFiltersToUrl(filters, currentPage);
  }, [filters, currentPage, syncFiltersToUrl]);

  // OBS-S1107-001 FIX (S1108): reset page to 1 when filter content actually changes
  const filtersJsonRef = React.useRef(JSON.stringify(filters));
  useEffect(() => {
    const newJson = JSON.stringify(filters);
    if (newJson !== filtersJsonRef.current) {
      filtersJsonRef.current = newJson;
      setCurrentPage(1);
    }
  }, [filters]);

  const [itemsPerPage, setItemsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(itemsPerPage, setItemsPerPage);

  // โหลดข้อมูลบริษัท
  const { data: companiesData } = useQuery({
    queryKey: [...queryKeys.companies.list()],
    queryFn: async () => await companyService.getCompanies({ page: 1, limit: 1000 }),
  });
  
  const companies = companiesData?.items || [];
  
  // ใช้ API จริง
  
  // Mutation hooks
  const createContractMutation = useCreateContract();
  const updateContractMutation = useUpdateContract();
  const deleteContractMutation = useDeleteContract();
  
  // React Query สำหรับดึงข้อมูล
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.contracts.list(), { 
      page: currentPage,
      limit: itemsPerPage,
      ...filters
    }],
    queryFn: async () => {
      const params: GetContractsParams = {
        page: currentPage,
        limit: itemsPerPage,
        search: filters.search || undefined,
        status: filters.status,
        company_id: filters.company_id,
        site_id: filters.site_id,
        from_date: filters.from_date,
        to_date: filters.to_date,
        is_expiring_soon: filters.is_expiring_soon,
        days_to_expiry: filters.days_to_expiry
      };
      return await contractService.getContracts(params);
    },
  });
  
  // ข้อมูลจาก React Query
  const contracts = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, itemsPerPage, setCurrentPage, isLoading);
  
  // Loading state จาก mutations
  const loading = isLoading || 
                 createContractMutation.isPending || 
                 updateContractMutation.isPending || 
                 deleteContractMutation.isPending;
  
  // Refetch เมื่อ filter เปลี่ยน
  useEffect(() => {
    refetch();
  }, [currentPage, itemsPerPage, filters]);
  
  // ใช้ API จริงแล้ว ไม่ต้องใช้ mock data

  // ฟังก์ชันสำหรับการค้นหา
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // รีเซ็ตหน้าเมื่อค้นหาใหม่
  };

  // ฟังก์ชันสำหรับล้างตัวกรอง
  const handleClearFilters = () => {
    setFilters({ search: '' });
    setCurrentPage(1);
  };

  // ฟังก์ชันสำหรับกรองสัญญาใกล้หมดอายุ
  const handleFilterExpiringContracts = (days: number = 30) => {
    setFilters({
      ...filters,
      status: ContractStatus.ACTIVE, // เฉพาะสัญญาที่ใช้งานอยู่
      is_expiring_soon: true,
      days_to_expiry: days
    });
    setCurrentPage(1);
  };

  // ฟังก์ชันสำหรับการลบสัญญา
  const handleDeleteContract = async (id: number) => {
    // FB-2026-0006: แนบท้ายร่าง = ปลดรายการเช่า (การเช่าใช้งานต่อ) — ข้อความต้องตรงกับ backend
    const target = contracts.find((c: any) => c.id === id);
    showConfirm(
      'ยืนยันการลบสัญญา',
      deleteContractConfirmMessage(target?.contract_number, target?.contract_type, target?.status),
      async () => {
        // BUG-S2089-001: ลบไม่สำเร็จ (สถานะเปลี่ยนไปแล้ว) ก็ต้องโหลดตารางใหม่
        deleteContractMutation.mutate(id, { onError: () => { refetch(); } });
      },
      { confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับการยกเลิกสัญญา
  const handleCancelContract = async (id: number) => {
    showConfirm(
      'ยืนยันการยกเลิกสัญญา',
      terminateContractConfirmMessage(
        contracts.find((c: any) => c.id === id)?.contract_number,
        contracts.find((c: any) => c.id === id)?.contract_type,
      ),
      async () => {
        try {
          await contractService.cancelContract(id);
          showAlert('ยกเลิกสัญญาสำเร็จ', 'สัญญาถูกยกเลิกเรียบร้อยแล้ว', 'success');
          // Refetch data immediately
          refetch();
        } catch (error) {
          console.error('Error cancelling contract:', error);
          // BUG-S2089-001: สถานะอาจถูกเปลี่ยนจากที่อื่น (อีกแท็บ/อีกคน) → โหลดตารางใหม่ให้เห็นสถานะจริง
          refetch();
          // BUG-S2073-002: เดิมทิ้งเหตุผลจาก backend (เช่น "ยังมีสัญญาลูกที่ใช้งานอยู่ …") — หน้ารายละเอียดแสดงถูกอยู่แล้ว
          showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถยกเลิกสัญญาได้'), 'error');
        }
      },
      { confirmText: 'ยกเลิกสัญญา', cancelText: 'ย้อนกลับ', isDanger: true }
    );
  };

  // ฟังก์ชันสำหรับการอนุมัติ/ปฏิเสธสัญญา
  const handleApproveContract = async (id: number, approved: boolean) => {
    const action = approved ? 'อนุมัติ' : 'ปฏิเสธ';
    const confirmTitle = approved ? 'ยืนยันการอนุมัติสัญญา' : 'ยืนยันการปฏิเสธสัญญา';
    const confirmMessage = approved
      ? 'คุณต้องการอนุมัติสัญญานี้ใช่หรือไม่? สัญญาจะเปลี่ยนสถานะเป็น "มีผลบังคับใช้"'
      : rejectContractConfirmMessage(
          contracts.find((c: any) => c.id === id)?.contract_number,
          contracts.find((c: any) => c.id === id)?.contract_type,
        );

    showConfirm(
      confirmTitle,
      confirmMessage,
      async () => {
        try {
          await contractService.approveContract(id, approved);
          showAlert(
            `${action}สัญญาสำเร็จ`,
            `สัญญาถูก${action}เรียบร้อยแล้ว`,
            'success'
          );
          // Refetch data immediately
          refetch();
        } catch (error) {
          console.error(`Error ${approved ? 'approving' : 'rejecting'} contract:`, error);
          // BUG-S2089-001: เดิมแถวยังแสดง "ร่างสัญญา" หลังชนกับผู้ใช้อื่น → ผู้ใช้กดซ้ำเรื่อย ๆ
          refetch();
          showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, `ไม่สามารถ${action}สัญญาได้`), 'error');
        }
      },
      { confirmText: action, cancelText: 'ย้อนกลับ', isDanger: !approved }
    );
  };

  // FB-2026-0006: โมดัล "เพิ่มสัญญาแนบท้าย" (สัญญาหลัก) / แก้ไขแนบท้ายสถานะร่าง
  const [addendumModal, setAddendumModal] = useState<{ main?: Contract | null; addendum?: Contract | null } | null>(null);

  // ฟังก์ชันสำหรับการแก้ไขสัญญา
  const handleEditContract = async (contract: Contract) => {
    // สัญญาแนบท้าย (ร่าง) แก้ผ่านโมดัลบันทึกส่ง-มอบ — ต้องโหลดรายละเอียด (contract_items) ก่อน
    if (contract.contract_type === 'ADDENDUM') {
      try {
        const full = await contractService.getContract(contract.id);
        setAddendumModal({ addendum: full });
      } catch (error) {
        showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถโหลดสัญญาแนบท้ายได้'), 'error');
      }
      return;
    }
    // ทุกสถานะไปหน้าแก้ไขเดิม (หน้าแก้ไขจะจัดการเหตุผลการแก้ไขเอง)
    navigate(`/contracts/edit/${contract.id}`);
  };

  // ฟังก์ชันสำหรับการเปลี่ยนหน้า
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  // สถานะสำหรับ loading ดาวน์โหลด PDF
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // ฟังก์ชันสำหรับดาวน์โหลดสัญญาเป็น PDF
  const handleDownloadPDF = async (contract: Contract, includeAddenda = false) => {
    try {
      setIsDownloadingPdf(true);
      await contractService.downloadAndSaveContractPDF(contract, includeAddenda ? { include_addenda: true } : undefined);
      showAlert('ดาวน์โหลดสำเร็จ', 'ไฟล์ PDF ถูกดาวน์โหลดเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดไฟล์ PDF ได้'), 'error');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // formatDate: ใช้ central utility จาก dateUtils.ts (BUG-S1043-001/002 fix S1045 + migrate S1046)
  // - honors user `displaySettings.dateFormat` ผ่าน module-level cache populated โดย AuthContext
  // - fallback เป็น 'th-TH' short locale เมื่อ user ไม่ได้ตั้งค่า (compat กับ behavior เดิม)
  // - ลบ local function เดิมที่ hardcode 'th-TH' (ไม่ระบุ timeZone — browser-local) ออก เพราะ ignore user preference

  // ฟังก์ชันสำหรับคำนวณและแสดงวันที่หมดอายุ
  const calculateDaysToExpiry = (endDate: string | undefined): { days: number; isExpiringSoon: boolean; isExpired: boolean } => {
    if (!endDate) return { days: 0, isExpiringSoon: false, isExpired: false };
    
    // BUG-S2075-002: เดิมเทียบ "เวลาตอนนี้" กับ end_date เป็นชั่วโมงแล้ว ceil → สัญญาที่สิ้นสุดวันนี้ 08:00
    // ขึ้น "หมดอายุแล้ว" (ทั้งที่ยัง ACTIVE และ backend นับว่าใกล้หมดอายุ) · สิ้นสุดวันนี้ 23:30 ขึ้น
    // "เหลือ 1 วัน" · สิ้นสุด +30 วันตอนค่ำ ไม่มีป้ายเลยทั้งที่อยู่ในตัวกรอง 30 วัน
    // วันสิ้นสุดสัญญาคือ "วันที่" (รวมวันนั้นทั้งวัน) → นับเป็นวันปฏิทิน ตรงกับ backend (func.date)
    const diffDays = calculateRemainingDays(new Date(endDate));

    return {
      days: diffDays,
      isExpiringSoon: diffDays <= 30 && diffDays >= 0,
      isExpired: diffDays < 0
    };
  };

  // ฟังก์ชันสำหรับแสดงสถานะการหมดอายุ
  const renderExpiryStatus = (contract: Contract): JSX.Element | null => {
    // FB-2026-0004: สัญญาหลัก/แนบท้าย = สัญญาแบบเปิด ไม่มีวันหมดอายุ (end_date = null)
    if (isOpenEndedContract(contract)) {
      return contract.status === ContractStatus.ACTIVE || contract.status === ContractStatus.DRAFT ? (
        <span className="text-xs text-secondary-500" data-testid="no-expiry">ไม่มีวันหมดอายุ</span>
      ) : null;
    }
    const { days, isExpiringSoon, isExpired } = calculateDaysToExpiry(contract.end_date ?? undefined);
    
    if (contract.status !== ContractStatus.ACTIVE) return null;
    
    if (isExpired) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-danger-100 text-danger-800">
          <RiCalendarCloseLine className="w-3 h-3 mr-1" />
          หมดอายุแล้ว
        </span>
      );
    }

    if (isExpiringSoon) {
      const bgColor = days <= 7 ? 'bg-danger-100 text-danger-800' : days <= 15 ? 'bg-warning-100 text-warning-800' : 'bg-warning-100 text-warning-800';
      return (
        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${bgColor}`}>
          <RiTimeLine className="w-3 h-3 mr-1" />
          {days === 0 ? 'หมดอายุวันนี้' : `เหลือ ${days} วัน`}
        </span>
      );
    }
    
    return null;
  };



  return (
    <Layout>
      <div className="w-full">
        {/* Page Header - Clean & Consistent */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiFileTextLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">
                จัดการสัญญา
                {filters.is_expiring_soon && (
                  <span className="ml-2 text-sm font-normal text-warning-600">
                    (ใกล้หมดอายุใน {filters.days_to_expiry} วัน)
                  </span>
                )}
              </h1>
              <p className="text-xs text-secondary-500 mt-0.5">
                {filters.is_expiring_soon
                  ? `แสดงสัญญาที่ใกล้หมดอายุภายใน ${filters.days_to_expiry} วัน`
                  : 'จัดการข้อมูลสัญญาทั้งหมด'
                }
              </p>
            </div>
          </div>
          {!loading && totalItems > 0 && (
            <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
              {totalItems.toLocaleString()} รายการ
            </span>
          )}
        </div>

        {/* Action Buttons Row */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleFilterExpiringContracts(7)}
                className={`inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] border border-warning-300 rounded-lg text-sm font-medium text-warning-700 bg-warning-50 hover:bg-warning-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning-500 shadow-button transition-colors ${
                  filters.is_expiring_soon && filters.days_to_expiry === 7
                    ? 'ring-2 ring-warning-400/40 bg-warning-100'
                    : ''
                }`}
              >
                <RiTimeLine className="-ml-1 mr-1 h-4 w-4" />
                หมดอายุใน 7 วัน
              </button>
              <button
                type="button"
                onClick={() => handleFilterExpiringContracts(30)}
                className={`inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] border border-warning-300 rounded-lg text-sm font-medium text-warning-700 bg-warning-50 hover:bg-warning-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning-500 shadow-button transition-colors ${
                  filters.is_expiring_soon && filters.days_to_expiry === 30
                    ? 'ring-2 ring-warning-400/40 bg-warning-100'
                    : ''
                }`}
              >
                <RiCalendarCheckLine className="-ml-1 mr-1 h-4 w-4" />
                หมดอายุใน 30 วัน
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/contracts/tracking')}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-colors"
              >
                <RiFileListLine className="mr-1 h-5 w-5" />
                ติดตามเอกสาร
              </button>
              <CanCreate module="contracts">
                <button
                  type="button"
                  onClick={() => navigate('/contracts/create')}
                  className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-colors"
                >
                  <RiAddLine className="mr-1 h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                  สร้างสัญญาใหม่
                </button>
              </CanCreate>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-secondary-200 px-4 py-3 mb-4">
          <div className="mb-3">
            <form onSubmit={handleSearch} noValidate className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <RiSearchLine className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="text"
                  className="input pl-10 pr-12"
                  placeholder="ค้นหาเลขที่สัญญา, ชื่อบริษัท..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {/* P-S665-1 FIX: ตัวกรองสถานะสัญญามีอยู่แล้วในการ์ดด้านล่าง แต่ปุ่มเปิดการ์ด
                      เป็นไอคอนจางๆ ไม่มี active state → ผู้ใช้หาไม่เจอ เข้าใจว่าหน้านี้กรอง
                      สถานะไม่ได้ (กรองได้ทาง URL เท่านั้น) · ปรับให้ตรงกับ /rentals/list
                      (RentalPage.tsx:931-943): tap target 44px, active state, aria-pressed,
                      aria-label สลับตามสถานะเปิด/ปิด */}
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-label={showFilters ? 'ซ่อนตัวกรอง' : 'แสดงตัวกรอง'}
                    aria-pressed={showFilters}
                    title={showFilters ? 'ซ่อนตัวกรอง' : 'แสดงตัวกรอง'}
                    className={cn(
                      'inline-flex items-center justify-center p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px]',
                      showFilters
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-secondary-600 hover:text-secondary-700 hover:bg-secondary-100'
                    )}
                  >
                    <RiFilterLine className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-colors"
              >
                ค้นหา
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-colors"
              >
                ล้างตัวกรอง
              </button>
              <button
                type="button"
                onClick={() => refetch()}
                className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-colors"
              >
                <RiRefreshLine className="mr-1 h-5 w-5 group-hover:rotate-180 transition-transform duration-500" />
                รีเฟรช
              </button>
            </form>
          </div>
          
          {showFilters && (
            <div className="mb-3 bg-secondary-50/70 p-4 rounded-xl border border-secondary-200">
              <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-1.5">
                <RiFilterLine className="w-4 h-4 text-secondary-400" />
                ตัวกรองเพิ่มเติม
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-secondary-700 mb-1">
                    บริษัท
                  </label>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'ทุกบริษัท' },
                      ...companies.map(company => ({ value: company.id, label: `${company.code} - ${company.name}` }))
                    ]}
                    value={filters.company_id || ''}
                    onChange={(value) => setFilters({ ...filters, company_id: value ? Number(value) : undefined })}
                    placeholder="ทุกบริษัท"
                    searchPlaceholder="ค้นหาบริษัท..."
                    name="company_filter"
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-secondary-700 mb-1">
                    สถานะสัญญา
                  </label>
                  <select
                    id="status"
                    className="select"
                    value={filters.status || ''}
                    onChange={(e) => setFilters({ ...filters, status: (e.target.value as ContractStatus) || undefined })}
                  >
                    <option value="">ทุกสถานะ</option>
                    <option value={ContractStatus.DRAFT}>ร่างสัญญา</option>
                    <option value={ContractStatus.ACTIVE}>มีผลบังคับใช้</option>
                    <option value={ContractStatus.EXPIRED}>หมดอายุ</option>
                    {/* P-S665-1 / SM8: ใช้คำเดียวกับ badge และปุ่มยกเลิกสัญญาทั้งระบบ
                        (statusColors.contract.TERMINATED ใน designSystem.ts) — เดิมแต่ละจุด
                        เขียนคนละคำ ("ยุติสัญญา"/"ยกเลิกแล้ว") ทำให้ผู้ใช้กรองแล้วนึกว่าได้ผลผิด */}
                    <option value={ContractStatus.TERMINATED}>ยกเลิกสัญญา</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="expiring-filter" className="block text-sm font-medium text-secondary-700 mb-1">
                    สัญญาใกล้หมดอายุ
                  </label>
                  <select
                    id="expiring-filter"
                    className="select"
                    value={filters.is_expiring_soon ? (filters.days_to_expiry || 30).toString() : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFilters({
                          ...filters,
                          is_expiring_soon: undefined,
                          days_to_expiry: undefined
                        });
                      } else {
                        const days = parseInt(value);
                        setFilters({
                          ...filters,
                          is_expiring_soon: true,
                          days_to_expiry: days,
                          status: ContractStatus.ACTIVE // เฉพาะสัญญาที่ใช้งานอยู่
                        });
                      }
                    }}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="7">หมดอายุใน 7 วัน</option>
                    <option value="15">หมดอายุใน 15 วัน</option>
                    <option value="30">หมดอายุใน 30 วัน</option>
                    <option value="60">หมดอายุใน 60 วัน</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="from-date" className="block text-sm font-medium text-secondary-700 mb-1">
                    ตั้งแต่วันที่
                  </label>
                  <input
                    type="date"
                    id="from-date"
                    className="input"
                    value={filters.from_date || ''}
                    onChange={(e) => setFilters({ ...filters, from_date: e.target.value || undefined })}
                  />
                </div>
                <div>
                  <label htmlFor="to-date" className="block text-sm font-medium text-secondary-700 mb-1">
                    ถึงวันที่
                  </label>
                  <input
                    type="date"
                    id="to-date"
                    className="input"
                    value={filters.to_date || ''}
                    onChange={(e) => setFilters({ ...filters, to_date: e.target.value || undefined })}
                  />
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-4 border border-danger-200 rounded-xl bg-danger-50">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-danger-100 flex items-center justify-center flex-shrink-0">
                  <RiFileTextLine className="w-4 h-4 text-danger-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-danger-800">เกิดข้อผิดพลาด</h3>
                  <p className="mt-0.5 text-sm text-danger-700">{getApiErrorMessage(error, 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ')}</p>
                </div>
                <button
                  onClick={() => refetch()}
                  className="px-3 py-1.5 text-xs font-medium text-danger-700 bg-danger-100 hover:bg-danger-200 rounded-lg transition-colors flex-shrink-0"
                >
                  ลองใหม่
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-10 h-10 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
              <p className="text-sm text-secondary-500 font-medium">กำลังโหลดข้อมูล...</p>
            </div>
          ) : error ? null /* BUG-S2273-001: โหลดล้ม → แบนเนอร์ error พอ ไม่ขึ้น "ไม่พบ…ตรงตามเงื่อนไข" + ปุ่มสร้างซ้อน */ : contracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary-50 ring-1 ring-secondary-200 flex items-center justify-center">
                <RiFileTextLine className="w-7 h-7 text-secondary-400" />
              </div>
              <div className="text-center">
                <h3 className="text-sm font-semibold text-secondary-800">ไม่พบสัญญา</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  ไม่พบสัญญาที่ตรงตามเงื่อนไขการค้นหา ลองปรับตัวกรองใหม่
                </p>
              </div>
              <CanCreate module="contracts">
                <Link
                  to="/contracts/create"
                  className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm transition-all duration-200"
                >
                  <RiAddLine className="mr-1.5 h-4 w-4" />
                  สร้างสัญญาใหม่
                </Link>
              </CanCreate>
            </div>
          ) : (
            <DualScrollWrapper className="border border-secondary-200 rounded-xl overflow-hidden">
              <table className="min-w-full divide-y divide-secondary-100" aria-label="รายการสัญญา">
                <thead className="bg-secondary-50 border-b-2 border-secondary-200 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      เลขที่สัญญา
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      บริษัท
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      ระยะเวลา
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      เงื่อนไขการชำระ
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      สถานะ
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">
                      การหมดอายุ
                    </th>
                    <th scope="col" className="sticky right-0 bg-secondary-50 px-3 py-3 text-center text-xs font-semibold text-secondary-600 whitespace-nowrap shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                      การดำเนินการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {contracts.map((contract) => (
                    <tr key={contract.id} className="group hover:bg-secondary-50/70 transition-colors duration-100">
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-semibold text-primary-700 hover:text-primary-800">
                          {contract.contract_number}
                        </span>
                        {/* FB-2026-0006: ประเภทสัญญา (สัญญาหลัก · แนบท้าย · แบบเดิม) */}
                        <div className="text-xs text-secondary-500 mt-0.5" data-testid="contract-kind">
                          {contract.contract_type === 'ADDENDUM' ? 'สัญญาแนบท้าย' : contractKindLabel(contract)}
                          {contract.contract_type === 'MAIN' && (contract.addendum_count ?? 0) > 0 && (
                            <> · แนบท้าย {contract.addendum_count} ฉบับ</>
                          )}
                        </div>
                        {contract.master_contract_id && (
                          <div className="text-xs text-secondary-400 mt-0.5">
                            {/* BUG-S2076-001: เดิมโชว์ id ในฐานข้อมูล ("สัญญาหลัก: 117") ผู้ใช้หาสัญญาหลักไม่เจอ */}
                            สัญญาหลัก:{' '}
                            <Link
                              to={`/contracts/view/${contract.master_contract_id}`}
                              className="underline hover:text-primary-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {contract.master_contract_number || `#${contract.master_contract_id}`}
                            </Link>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-secondary-800">{contract.company?.name || contract.company_name || '-'}</span>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        {isOpenEndedContract(contract) ? (
                          <>
                            <div className="text-sm text-secondary-700">
                              {contract.contract_type === 'ADDENDUM' ? 'เริ่มเช่า ' : 'วันที่ทำสัญญา '}
                              {formatDate(contract.contract_type === 'ADDENDUM' ? contract.start_date : (contract.contract_date || contract.start_date))}
                            </div>
                            <div className="text-xs text-secondary-600">สัญญาแบบเปิด</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-secondary-700">{formatDate(contract.start_date)}</div>
                            <div className="text-xs text-secondary-600">ถึง {formatDate(contract.end_date ?? undefined)}</div>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap text-sm text-secondary-600">
                        {contract.payment_term === 'MONTHLY' ? 'รายเดือน'
                          : contract.payment_term === 'QUARTERLY' ? 'รายไตรมาส'
                          : contract.payment_term === 'YEARLY' ? 'รายปี'
                          : <span className="text-secondary-300">—</span>}
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <ContractStatusBadge status={contract.status as any} size="sm" bordered />
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap text-sm text-secondary-500">
                        {renderExpiryStatus(contract)}
                      </td>
                      <td className="sticky right-0 bg-white group-hover:bg-secondary-50/70 px-3 py-3.5 whitespace-nowrap text-sm text-secondary-500 text-right shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.06)] transition-colors duration-100">
                        <ActionDropdown
                          buttonIcon={<RiMore2Fill className="w-4 h-4" />}
                          buttonLabel=""
                          actions={[
                            // View actions
                            ...(canView('contracts') ? [
                              {
                                label: 'ดูรายละเอียด',
                                icon: <RiEyeLine className="w-4 h-4" />,
                                onClick: () => navigate(`/contracts/view/${contract.id}`)
                              }
                            ] : []),

                            // Edit actions
                            // BUG-S1532-001 FIX: ContractStatus enum (contractService.ts) มีแค่
                            // DRAFT/ACTIVE/EXPIRED/TERMINATED — ไม่มี PENDING (PENDING เป็นของ
                            // DocumentStatus คนละ enum). `ContractStatus.PENDING` = undefined →
                            // เงื่อนไขนี้เป็น dead clause. ตัด PENDING ออก
                            // แนบท้ายแก้ได้เฉพาะร่าง (backend PUT /contracts/{id}/addendum)
                            ...(canEdit('contracts') && (contract.contract_type === 'ADDENDUM'
                              ? contract.status === ContractStatus.DRAFT
                              : (contract.status === ContractStatus.DRAFT || contract.status === ContractStatus.ACTIVE)) ? [
                              {
                                label: contract.contract_type === 'ADDENDUM' ? 'แก้ไขสัญญาแนบท้าย' : 'แก้ไขสัญญา',
                                icon: <RiEditLine className="w-4 h-4" />,
                                onClick: () => handleEditContract(contract)
                              }
                            ] : []),

                            // FB-2026-0006: เพิ่มสัญญาแนบท้าย (บันทึกส่ง-มอบ) จากแถวสัญญาหลัก
                            ...(hasPermission('contracts.create') && contract.contract_type === 'MAIN'
                              && (contract.status === ContractStatus.DRAFT || contract.status === ContractStatus.ACTIVE) ? [
                              {
                                label: 'เพิ่มสัญญาแนบท้าย',
                                icon: <RiAddLine className="w-4 h-4" />,
                                onClick: () => setAddendumModal({ main: contract }),
                                className: 'text-primary-700 hover:text-primary-800 hover:bg-primary-50'
                              }
                            ] : []),

                            // Download and tracking actions
                            ...(hasPermission('contracts.view') ? [
                              { divider: true },
                              {
                                label: contract.contract_type === 'ADDENDUM' ? 'ดาวน์โหลด PDF บันทึกส่ง-มอบ' : 'ดาวน์โหลด PDF',
                                icon: <RiFileDownloadLine className="w-4 h-4" />,
                                onClick: () => handleDownloadPDF(contract)
                              },
                              ...(contract.contract_type === 'MAIN' && (contract.addendum_count ?? 0) > 0 ? [{
                                label: 'ดาวน์โหลด PDF สัญญาหลัก + แนบท้ายทั้งหมด',
                                icon: <RiFileDownloadLine className="w-4 h-4" />,
                                onClick: () => handleDownloadPDF(contract, true)
                              }] : [])
                            ] : []),
                            ...(hasPermission('contracts.view') ? [
                              {
                                label: 'ติดตามการส่งสัญญา',
                                icon: <RiMailSendLine className="w-4 h-4" />,
                                onClick: () => navigate(`/contracts/tracking?contract_id=${contract.id}`),
                                className: 'text-info-600 hover:text-info-700 hover:bg-info-50'
                              }
                            ] : []),

                            // Sign action
                            // BUG-S2077-002: เดิมเช็คแค่ customer_signed_at ที่ API ไม่เคยส่งมา → เมนูโชว์ตลอดแม้เซ็นแล้ว
                            // เมนูนี้พาไปหน้าติดตามเอกสาร → ถือว่าเซ็นแล้วเมื่อเอกสารถึงขั้นลูกค้าเซ็น/เสร็จสมบูรณ์ ด้วย
                            ...(hasPermission('contracts.approve') && contract.status === ContractStatus.ACTIVE && !isContractSigned(contract) ? [
                              { divider: true },
                              {
                                label: 'ลงนามสัญญา',
                                icon: <RiQuillPenLine className="w-4 h-4" />,
                                // BUG-SM8-SIGNROUTE: `/contracts/sign/:id` ไม่มี route ใน App.tsx
                                // → NotFound ทุกครั้ง · flow ลงนามจริงอยู่ในหน้าติดตามเอกสารสัญญา
                                onClick: () => navigate(`/contracts/tracking?contract_id=${contract.id}`),
                                className: 'text-success-600 hover:text-success-700 hover:bg-success-50'
                              }
                            ] : []),

                            // Approve/Reject actions
                            ...(hasPermission('contracts.approve') && contract.status === ContractStatus.DRAFT ? [
                              { divider: true },
                              {
                                label: 'อนุมัติสัญญา',
                                icon: <RiCheckLine className="w-4 h-4" />,
                                onClick: () => handleApproveContract(contract.id, true),
                                className: 'text-success-600 hover:text-success-700 hover:bg-success-50'
                              },
                              {
                                label: 'ปฏิเสธสัญญา',
                                icon: <RiCloseLine className="w-4 h-4" />,
                                onClick: () => handleApproveContract(contract.id, false),
                                className: 'text-warning-600 hover:text-warning-700 hover:bg-warning-50'
                              }
                            ] : []),

                            // Cancel action — backend terminate_contract requires ACTIVE
                            // (BUG-S1532-001: ContractStatus.PENDING dead clause removed)
                            ...(hasPermission('contracts.terminate') && contract.status === ContractStatus.ACTIVE ? [
                              { divider: true },
                              {
                                label: 'ยกเลิกสัญญา',
                                icon: <RiCalendarCloseLine className="w-4 h-4" />,
                                onClick: () => handleCancelContract(contract.id),
                                className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
                              }
                            ] : []),

                            // Delete action
                            ...(canDelete('contracts') && (contract.status === ContractStatus.DRAFT || contract.status === ContractStatus.TERMINATED) ? [
                              { divider: true },
                              {
                                label: 'ลบสัญญา',
                                icon: <RiDeleteBinLine className="w-4 h-4" />,
                                onClick: () => handleDeleteContract(contract.id),
                                className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
                              }
                            ] : [])
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DualScrollWrapper>
          )}

          {/* Pagination */}
          {!loading && contracts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              pageSize={itemsPerPage}
              onPageChange={handlePageChange}
              onPageSizeChange={(newPageSize) => {
                setItemsPerPage(newPageSize);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              showSummary={true}
              className="mt-5"
            />
          )}
        </div>
      </div>

      <AddendumFormModal
        isOpen={addendumModal !== null}
        onClose={() => setAddendumModal(null)}
        mainContract={addendumModal?.main ?? null}
        addendum={addendumModal?.addendum ?? null}
        onSaved={() => refetch()}
      />

      {/* Loading Overlay สำหรับดาวน์โหลด PDF */}
      <LoadingOverlayModal
        isOpen={isDownloadingPdf}
        icon="pdf"
        title="กำลังสร้างเอกสารสัญญา PDF"
        submessage="กรุณารอสักครู่ ระบบกำลังประมวลผลเอกสาร"
      />
    </Layout>
  );
};

export default ContractPage;